// Writeups.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faUser, faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import API from '../api';
import { categories } from '../modules/home/writeupdata';
import './Writeups.css';

function Writeups() {
    const [writeups, setWriteups] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentUser, setCurrentUser] = useState('');
    const [editingWriteup, setEditingWriteup] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        url: '',
        category: 'OSINT',
        description: '',
        read_time: '',
        ctf: ''
    });
    const [error, setError] = useState('');

    useEffect(() => {
        fetchWriteups();
        fetchCurrentUser();
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const response = await fetch(API.USER_PROFILE, {
                credentials: 'include'
            });
            const data = await response.json();
            setCurrentUser(data.username);
        } catch (error) {
            console.error('Error fetching user:', error);
        }
    };

    const fetchWriteups = async () => {
        try {
            const response = await fetch(API.WRITEUPS, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setWriteups(data.writeups);
            }
        } catch (error) {
            console.error('Error fetching writeups:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const url = API.WRITEUPS;
        const method = editingWriteup ? 'PUT' : 'POST';
        const submitData = editingWriteup ? { ...formData, id: editingWriteup.id } : formData;

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(submitData)
            });

            const data = await response.json();
            if (data.status === 'success') {
                setShowModal(false);
                fetchWriteups();
                setEditingWriteup(null);
                setFormData({
                    title: '',
                    url: '',
                    category: 'OSINT',
                    description: '',
                    read_time: '',
                    ctf: ''
                });
            } else {
                setError(data.message);
            }
        } catch (error) {
            setError('Failed to save writeup');
            console.error('Error:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this writeup?')) {
            return;
        }

        try {
            const response = await fetch(API.WRITEUPS_DELETE(id), {
                method: 'DELETE',
                credentials: 'include'
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                fetchWriteups();
            } else {
                alert(data.message);
            }
        } catch (error) {
            console.error('Error deleting writeup:', error);
            alert('Failed to delete writeup');
        }
    };

    const handleEdit = (writeup) => {
        setEditingWriteup(writeup);
        setFormData({
            title: writeup.title,
            url: writeup.url,
            category: writeup.category,
            description: writeup.description,
            read_time: writeup.read_time,
            ctf: writeup.ctf
        });
        setShowModal(true);
    };

    return (
        <Container fluid className="writeups20-page-wrapper">
            <div className="writeups20-main-wrapper">
                <div className="writeups20-header-wrapper">
                    <h2>Write-ups</h2>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                        className="writeups20-create-button"
                    >
                        Create Write-up
                    </Button>
                </div>

                <div className="writeups20-grid-wrapper">
                    <Row>
                        {writeups.map((writeup) => (
                            <Col key={writeup.id} md={6} lg={4} className="writeups20-grid-col">
                                <div className="writeups20-card">
                                    <div className="writeups20-card-header">
                                        <div className="writeups20-meta-top">
                                            <span className="writeups20-date">{writeup.date}</span>
                                            <div 
                                                className="writeups20-category-icon"
                                                style={{
                                                    background: categories[writeup.category]?.gradient
                                                }}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={categories[writeup.category]?.icon} 
                                                    className="icon"
                                                />
                                            </div>
                                        </div>
                                        {currentUser === writeup.author && (
                                            <div className="writeups20-actions">
                                                <Button 
                                                    variant="link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleEdit(writeup);
                                                    }}
                                                    className="writeups20-edit-btn"
                                                >
                                                    <FontAwesomeIcon icon={faPencilAlt} />
                                                </Button>
                                                <Button 
                                                    variant="link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleDelete(writeup.id);
                                                    }}
                                                    className="writeups20-delete-btn"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <a 
                                        href={writeup.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="writeups20-card-link"
                                    >
                                        <div className="writeups20-content">
                                            <div className="writeups20-meta">
                                                <span className="writeups20-category">
                                                    {writeup.category}
                                                </span>
                                            </div>
                                            <h3 className="writeups20-title">
                                                {writeup.title}
                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-2" />
                                            </h3>
                                            <p className="writeups20-description">
                                                {writeup.description}
                                            </p>
                                            <div className="writeups20-author">
                                                <FontAwesomeIcon icon={faUser} className="author-icon" />
                                                <span>{writeup.author}</span>
                                            </div>
                                            <div className="writeups20-footer">
                                                <span className="writeups20-ctf">
                                                    {writeup.ctf}
                                                </span>
                                                <span className="writeups20-readtime">
                                                    {writeup.read_time}
                                                </span>
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>

                <Modal 
                    show={showModal} 
                    onHide={() => {
                        setShowModal(false);
                        setEditingWriteup(null);
                        setFormData({
                            title: '',
                            url: '',
                            category: 'OSINT',
                            description: '',
                            read_time: '',
                            ctf: ''
                        });
                        setError('');
                    }}
                    size="lg"
                    className="writeups-modal"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {editingWriteup ? 'Edit Write-up' : 'Create New Write-up'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {error && (
                            <div className="alert alert-danger">{error}</div>
                        )}
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>URL</Form.Label>
                                <Form.Control
                                    type="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Category</Form.Label>
                                <Form.Select
                                    value={formData.category}
                                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                                    required
                                >
                                    {Object.keys(categories).map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Read Time (e.g., "5 min read")</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.read_time}
                                    onChange={(e) => setFormData({...formData, read_time: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>CTF Event</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.ctf}
                                    onChange={(e) => setFormData({...formData, ctf: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <div className="d-flex justify-content-end gap-2">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => {
                                        setShowModal(false);
                                        setEditingWriteup(null);
                                        setError('');
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button variant="primary" type="submit">
                                    {editingWriteup ? 'Update' : 'Create'}
                                </Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>
            </div>
        </Container>
    );
}

export default Writeups;